import { useState } from "react";
import { Mail, Phone, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/site/PageHero";
import { SectionLabel } from "../components/site/SectionLabel";
import { submitEnquiry, trackEvent } from "../lib/api";
import { AREAS_OF_INTEREST, CONTACT } from "../data/site";

const EMPTY = { name: "", company: "", email: "", country: "", area_of_interest: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.area_of_interest || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      trackEvent("contact_submission", form.area_of_interest);
      setDone(true);
      setForm(EMPTY);
      toast.success("Your enquiry has been sent. Thank you.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "h-12 w-full border border-brand-line bg-white px-3 text-sm text-brand-ink outline-none focus:ring-2 focus:ring-brand-blue";
  const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-brand-muted";

  return (
    <div>
      <PageHero
        variant="dark"
        label="Contact"
        title="Discuss a Business Challenge"
        intro="Tell us about the market, opportunity or industrial question you are facing. We will suggest whether and how MATRIVON can provide an independent perspective."
        crumbs={[{ label: "Contact" }]}
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.6fr_1fr]">
            {/* Form */}
            <div>
              {done ? (
                <div data-testid="contact-success" className="flex flex-col items-start gap-4 border border-brand-blue/20 bg-brand-blue/5 p-10">
                  <div className="flex h-12 w-12 items-center justify-center bg-brand-blue text-white">
                    <Check className="h-6 w-6" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-brand-ink">Enquiry received</h2>
                  <p className="text-brand-muted">
                    Thank you for reaching out to MATRIVON. Your enquiry has been recorded and we will
                    respond directly to the email you provided.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue"
                  >
                    Send another enquiry <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Name *</label>
                      <input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} required />
                    </div>
                    <div>
                      <label className={labelCls}>Company</label>
                      <input data-testid="contact-company" className={inputCls} value={form.company} onChange={set("company")} />
                    </div>
                    <div>
                      <label className={labelCls}>Business Email *</label>
                      <input type="email" data-testid="contact-email" className={inputCls} value={form.email} onChange={set("email")} required />
                    </div>
                    <div>
                      <label className={labelCls}>Country</label>
                      <input data-testid="contact-country" className={inputCls} value={form.country} onChange={set("country")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Area of Interest *</label>
                    <select data-testid="contact-area" className={inputCls} value={form.area_of_interest} onChange={set("area_of_interest")} required>
                      <option value="">Select an area…</option>
                      {AREAS_OF_INTEREST.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Message *</label>
                    <textarea data-testid="contact-message" rows={6} className="w-full border border-brand-line bg-white p-3 text-sm text-brand-ink outline-none focus:ring-2 focus:ring-brand-blue" value={form.message} onChange={set("message")} required />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="inline-flex items-center gap-2 bg-brand-red px-8 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
                  >
                    {loading ? "Sending…" : <>Send Enquiry <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>
              )}
            </div>

            {/* Aside */}
            <aside className="space-y-6">
              <div className="border border-brand-line bg-brand-bg p-6">
                <SectionLabel>Direct contact</SectionLabel>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-direct-email" className="flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue">
                  <Mail className="h-4 w-4 text-brand-blue" /> {CONTACT.email}
                </a>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-direct-phone" className="mt-3 flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue">
                  <Phone className="h-4 w-4 text-brand-blue" /> {CONTACT.phone}
                </a>
              </div>

              <div className="border border-brand-line bg-brand-ink p-6 text-white">
                <h3 className="font-heading text-lg font-bold">How engagements work</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  From a focused strategic conversation to a defined project or ongoing retained
                  advisory — the engagement depends on the decision. Fees are agreed per engagement.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
